import { useCallback, useRef } from 'react';
import CodeEditor from 'react-simple-code-editor';

import { useEditorSyntaxStyle } from '@/_shared/hooks/useSyntaxStyle';
import { formatSql } from '@/_shared/lib/sql';
import { SyntaxHighlighterSync } from '@/_shared/lib/syntax';
import { cn } from '@/_shared/lib/utils';

// react-simple-code-editor ships as CJS (`exports.default = Editor`). Vite's browser build hands
// back the module namespace ({ default: Editor }) rather than the component, so unwrap to the real
// default. Vitest's interop already unwraps it — the `?? CodeEditor` fallback keeps that path working.
const Editor = (CodeEditor as unknown as { default?: typeof CodeEditor }).default ?? CodeEditor;

interface SqlEditorProps {
  value: string;
  onChange: (sql: string) => void;
  /** DOM id for the underlying textarea so an external `<Label htmlFor>` associates with it. */
  id?: string;
  /** Fired when the editor loses focus — used by the caller to mark the field touched for validation. */
  onBlur?: () => void;
  /** Show the field in the invalid (destructive) state — a red border matching the `Input` primitive. */
  invalid?: boolean;
  /**
   * Read-only (the agent owns the draft mid-turn): highlighted + selectable but not editable, and
   * the Format action is hidden. Mirrors the primitive `Textarea`'s read-only treatment.
   */
  readOnly?: boolean;
}

/** Live-highlight for the editor overlay — token colors from the theme-selected style, metrics inherited. */
function highlightSql(code: string, style: ReturnType<typeof useEditorSyntaxStyle>): React.ReactNode {
  return (
    <>
      <SyntaxHighlighterSync language="sql" style={style} PreTag="span" CodeTag="span">
        {code}
      </SyntaxHighlighterSync>
      {/* react-simple-code-editor appends a trailing `<br />` only when `highlight` returns a STRING;
          we return a node, so mirror it here — otherwise the highlight layer is one line short of the
          textarea and the caret desyncs on a trailing newline. */}
      <br />
    </>
  );
}

/**
 * SQL editor for the workflow query step — a syntax-highlighted textarea (react-simple-code-editor
 * layered over the shared Prism theme). A single bordered box so the focus ring reads as one control.
 *
 * Formatting is automatic where SQL *arrives* rather than where it's typed: the caller formats on
 * load and agent-fill (see `useRoutineDraft`), this component formats on paste, and the caller's
 * Format button (rendered beside the field label) formats on demand. Typing is left byte-exact —
 * reflowing every keystroke would fight the caret. Every trigger routes through the same `onChange`.
 */
export function SqlEditor({
  value,
  onChange,
  id,
  onBlur,
  invalid = false,
  readOnly = false,
}: SqlEditorProps): React.ReactElement {
  // A paste is the one keystroke-adjacent action we auto-format. The native paste event fires (and
  // bubbles to this wrapper) before react-simple-code-editor's change, so we flag it here and
  // reformat in `handleValueChange` once the pasted text has landed.
  const pastedRef = useRef(false);
  const editorStyle = useEditorSyntaxStyle();

  // Last-value memo for the live highlight. react-simple-code-editor calls `highlight(code)` on every
  // render — not just keystrokes but focus/blur, selection, and parent re-renders — and each call
  // rebuilds the Prism token tree. Caching the most recent (code → node) collapses those redundant
  // rebuilds to a reference compare, so only a genuine text change re-tokenizes. The style is part of
  // the cache key so a theme switch (new `editorStyle`) re-tokenizes with the new token colors.
  const highlightCacheRef = useRef<{
    code: string;
    style: typeof editorStyle;
    node: React.ReactNode;
  } | null>(null);
  const highlight = useCallback(
    (code: string): React.ReactNode => {
      const cached = highlightCacheRef.current;
      if (cached?.code === code && cached.style === editorStyle) return cached.node;
      const node = highlightSql(code, editorStyle);
      highlightCacheRef.current = { code, style: editorStyle, node };
      return node;
    },
    [editorStyle],
  );

  function handleValueChange(next: string): void {
    if (pastedRef.current) {
      pastedRef.current = false;
      onChange(formatSql(next));
      return;
    }
    onChange(next);
  }

  // Mirrors the `Input` primitive. Hover glow is scoped to `:not(:focus-within)` so the focus ring
  // always wins (both set `box-shadow`, and Tailwind emits `hover` after `focus-within`). The three
  // states are mutually exclusive so only ONE hover-shadow class is emitted — otherwise the grey
  // secondary glow would leak into the invalid state on hover.
  const boxClassName = cn(
    'rounded-md border border-grey-75 bg-grey-0 transition-[color,box-shadow,border-color]',
    readOnly && 'bg-grey-25 focus-within:shadow-secondary',
    !readOnly &&
      !invalid &&
      'focus-within:border-primary focus-within:shadow-primary [&:hover:not(:focus-within)]:shadow-secondary',
    // Invalid mirrors the `Input` primitive's `aria-invalid` treatment — destructive border + a red
    // focus AND hover glow (the hover glow must be destructive too, not the grey secondary shadow).
    !readOnly &&
      invalid &&
      'border-destructive focus-within:border-destructive focus-within:shadow-destructive [&:hover:not(:focus-within)]:shadow-destructive',
  );

  return (
    <div
      onPaste={() => {
        pastedRef.current = true;
        // Auto-disarm once this gesture settles. A paste that inserts nothing (empty clipboard, or a
        // read-only editor) fires no `onValueChange` to consume the flag, so without this it would
        // linger and reformat the next keystroke. The paste's own `onValueChange` runs synchronously
        // within this task, before this macrotask timer, so a real paste is still reformatted.
        window.setTimeout(() => {
          pastedRef.current = false;
        }, 0);
      }}
      className={boxClassName}
    >
      {/* Scroll lives on this inner wrapper, never the bordered box: `overflow` on the shadowed
          element clips its focus ring into square corners. The editor grows freely inside and this
          caps the visible height at 400px (25rem), scrolling the whole block (textarea + highlight
          layer together) once a query is taller than that. */}
      <div className="max-h-[25rem] overflow-y-auto">
        <Editor
          value={value}
          onValueChange={handleValueChange}
          onBlur={onBlur}
          highlight={highlight}
          readOnly={readOnly}
          textareaId={id}
          tabSize={2}
          insertSpaces
          padding="0.5rem"
          className="font-mono text-[0.8125rem] leading-relaxed text-grey-800"
          // Transparent so the rounded container's fill shows (its default white bg is a square that
          // would otherwise poke past the rounded corners); `outline-none` kills the browser's outline.
          textareaClassName="bg-transparent outline-none"
          style={{ minHeight: '5.5rem' }}
        />
      </div>
    </div>
  );
}
